import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { decodeToken, useTokenExpiration } from '../utils/token'
import { toast } from 'react-toastify'

const AuthContext = createContext(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'))
  const [email, setEmail] = useState(localStorage.getItem('email'))
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('darkMode') !== 'false'
  )
  const [isAdmin, setIsAdmin] = useState(false)
  const [userType, setUserType] = useState(null)  // 'env_admin' | 'invited'
  const [showExpirationModal, setShowExpirationModal] = useState(false)

  // Handle token expiration
  const handleTokenExpired = useCallback(() => {
    logout()
    toast.error('Sua sessão expirou. Por favor, faça login novamente.')
  }, [])

  // Token expiration management
  const { timeRemaining, isExpiring } = useTokenExpiration(
    token,
    handleTokenExpired
  )

  // Show expiration modal when expiring
  useEffect(() => {
    if (isExpiring && timeRemaining) {
      setShowExpirationModal(true)
    } else {
      setShowExpirationModal(false)
    }
  }, [isExpiring, timeRemaining])

  // Apply dark mode class to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    localStorage.setItem('darkMode', isDarkMode)
  }, [isDarkMode])

  // Decode JWT token to extract isAdmin and userType
  useEffect(() => {
    if (token) {
      const decoded = decodeToken(token)
      const type = decoded?.user_type
      setUserType(type || null)
      setIsAdmin(type === 'env_admin')
    } else {
      setIsAdmin(false)
      setUserType(null)
    }
  }, [token])

  const login = (newToken, userEmail) => {
    setToken(newToken)
    setEmail(userEmail)
    localStorage.setItem('token', newToken)
    localStorage.setItem('email', userEmail)
  }

  const logout = useCallback(() => {
    setToken(null)
    setEmail(null)
    setIsAdmin(false)
    setUserType(null)
    localStorage.removeItem('token')
    localStorage.removeItem('email')
  }, [])

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode)
  }

  const isAuthenticated = !!token

  // Memoize context value to prevent unnecessary re-renders
  // Note: timeRemaining, isExpiring, showExpirationModal are excluded from deps
  // to avoid triggering re-renders across the entire app every 30s
  const contextValue = useMemo(
    () => ({
      token,
      email,
      isDarkMode,
      isAdmin,
      userType,
      isAuthenticated,
      login,
      logout,
      toggleDarkMode,
      timeRemaining,
      isExpiring,
      showExpirationModal,
      setShowExpirationModal
    }),
    [
      token,
      email,
      isDarkMode,
      isAdmin,
      userType,
      isAuthenticated,
      login,
      logout
    ]
  )

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}
